const TOKEN_KEY = 'dari_admin_token';
const API_BASE = '/api';

async function sha256Hex(text) {
  const buf = new TextEncoder().encode(text);
  const hashBuf = await crypto.subtle.digest('SHA-256', buf);
  return Array.from(new Uint8Array(hashBuf))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

export function getToken() {
  try { return sessionStorage.getItem(TOKEN_KEY); } catch (_) { return null; }
}

function setToken(t) {
  try {
    if (t) sessionStorage.setItem(TOKEN_KEY, t);
    else sessionStorage.removeItem(TOKEN_KEY);
  } catch (_) {}
}

export async function login(password) {
  const res = await fetch(`${API_BASE}/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ password }),
  });
  if (!res.ok) {
    if (res.status === 401) throw new Error('Incorrect password');
    throw new Error('Login failed');
  }
  const { token } = await res.json();
  setToken(token);
  return token;
}

export async function logout() {
  const token = getToken();
  setToken(null);
  if (token) {
    try {
      await fetch(`${API_BASE}/logout`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch (_) {}
  }
}

export async function checkAuth() {
  const token = getToken();
  if (!token) return false;
  try {
    const res = await fetch(`${API_BASE}/auth/check`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (res.status === 401) { setToken(null); return false; }
    return res.ok;
  } catch (_) { return false; }
}

// Returns Set of word strings that have a recording.
export async function listRecordedWords(words) {
  const res = await fetch(`${API_BASE}/recordings`);
  if (!res.ok) return new Set();
  const { recorded } = await res.json();
  const recordedHashes = new Set(recorded);
  const out = new Set();
  await Promise.all(words.map(async (w) => {
    const h = await sha256Hex(w);
    if (recordedHashes.has(h)) out.add(w);
  }));
  return out;
}

export function audioUrlFor(word) {
  return `${API_BASE}/recordings/${encodeURIComponent(word)}`;
}

let _audioCtx = null;
function getAudioCtx() {
  if (!_audioCtx) {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return null;
    _audioCtx = new Ctx();
  }
  if (_audioCtx.state === 'suspended') _audioCtx.resume().catch(() => {});
  return _audioCtx;
}

const decodeCache = new Map();

export async function playLoud(url, gain = 3.5) {
  const ctx = getAudioCtx();
  if (!ctx) {
    // Fallback to plain audio element
    new Audio(url).play().catch(() => {});
    return;
  }
  let buffer = decodeCache.get(url);
  if (!buffer) {
    const res = await fetch(url);
    if (!res.ok) return;
    const arr = await res.arrayBuffer();
    buffer = await new Promise((resolve, reject) =>
      ctx.decodeAudioData(arr, resolve, reject)
    );
    decodeCache.set(url, buffer);
  }
  const source = ctx.createBufferSource();
  source.buffer = buffer;

  const compressor = ctx.createDynamicsCompressor();
  compressor.threshold.value = -24;
  compressor.knee.value = 12;
  compressor.ratio.value = 4;
  compressor.attack.value = 0.003;
  compressor.release.value = 0.15;

  const gainNode = ctx.createGain();
  gainNode.gain.value = gain;

  source.connect(compressor).connect(gainNode).connect(ctx.destination);
  source.start();
}

export function invalidateAudioCache(word) {
  if (!word) { decodeCache.clear(); return; }
  decodeCache.delete(audioUrlFor(word));
  // Also clear any cache-busted variants
  for (const k of decodeCache.keys()) {
    if (k.startsWith(audioUrlFor(word))) decodeCache.delete(k);
  }
}

// HEAD-ish probe: try fetching the audio; returns Blob or null.
export async function getRecording(word) {
  const res = await fetch(audioUrlFor(word));
  if (res.status === 404) return null;
  if (!res.ok) return null;
  return await res.blob();
}

export async function saveRecording(word, blob) {
  const token = getToken();
  if (!token) throw new Error('Not logged in');
  const res = await fetch(audioUrlFor(word), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': blob.type || 'audio/webm',
    },
    body: blob,
  });
  if (res.status === 401) { setToken(null); throw new Error('Session expired'); }
  if (!res.ok) throw new Error('Save failed');
  return await res.json();
}

export async function deleteRecording(word) {
  const token = getToken();
  if (!token) throw new Error('Not logged in');
  const res = await fetch(audioUrlFor(word), {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${token}` },
  });
  if (res.status === 401) { setToken(null); throw new Error('Session expired'); }
  if (!res.ok) throw new Error('Delete failed');
}
